// =============================================================================
// PPIInscricao — página PÚBLICA de inscrição no PPI (Programa de Promoção Interna)
// =============================================================================
// Rota /ppi-inscricao/:token (sem login). O funcionário informa o CPF (validado
// contra a dtbConfere no backend) e escolhe a vaga. Molde: a tela pública do
// Processo Seletivo. fetch em caminho relativo /api/... (sem Authorization).

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';

// Valida CPF pelos dígitos verificadores (mesma regra do backend — evita ida
// desnecessária ao servidor com CPF obviamente inválido).
function validarCPF(cpf) {
    const c = String(cpf || '').replace(/[^\d]/g, '');
    if (c.length !== 11 || /^(\d)\1+$/.test(c)) return false;
    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(c.charAt(i)) * (10 - i);
    let d1 = soma % 11 >= 2 ? 11 - (soma % 11) : 0;
    if (d1 !== parseInt(c.charAt(9))) return false;
    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(c.charAt(i)) * (11 - i);
    let d2 = soma % 11 >= 2 ? 11 - (soma % 11) : 0;
    return d2 === parseInt(c.charAt(10));
}

// Máscara simples de CPF (000.000.000-00) enquanto digita.
function mascararCPF(v) {
    const d = String(v).replace(/\D/g, '').slice(0, 11);
    return d
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

const COR = '#0284c7';   // azul do módulo Treinamento (o PPI migrou do RH em 2026-07-15)

function PPIInscricao() {
    const { token } = useParams();
    const [carregando, setCarregando] = useState(true);
    const [processo, setProcesso] = useState(null);
    const [erroCarga, setErroCarga] = useState('');

    const [cpf, setCpf] = useState('');
    const [idVaga, setIdVaga] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState(null); // { nome, vaga }

    useEffect(() => {
        (async () => {
            try {
                const resp = await fetch(`/api/treinamento/ppi/public/processo/${token}`);
                if (!resp.ok) throw new Error('Inscrições não disponíveis.');
                setProcesso(await resp.json());
            } catch (e) {
                setErroCarga(e.message || 'Inscrições não disponíveis.');
            } finally {
                setCarregando(false);
            }
        })();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErro('');
        if (!validarCPF(cpf)) { setErro('CPF inválido. Confira os números digitados.'); return; }
        if (!idVaga) { setErro('Selecione a vaga desejada.'); return; }
        setEnviando(true);
        try {
            const resp = await fetch('/api/treinamento/ppi/public/inscricao', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, cpf: cpf.replace(/\D/g, ''), idVaga: Number(idVaga) }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.message || 'Não foi possível concluir a inscrição.');
            setSucesso({ nome: data.nome, vaga: data.vaga });
        } catch (err) {
            setErro(err.message);
        } finally {
            setEnviando(false);
        }
    };

    const pagina = (conteudo) => (
        <div style={{ backgroundColor: '#f1f5f9', minHeight: '100vh', padding: '40px 20px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            <div style={{ width: '100%', maxWidth: '560px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <img alt="Grupo Correia" src="/icon-logotipo.png" style={{ height: '110px' }} />
                </div>
                {conteudo}
            </div>
        </div>
    );

    if (carregando) {
        return pagina(
            <div style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>
                <Loader2 size={28} style={{ animation: 'spin 1s linear infinite' }} /> Carregando…
            </div>
        );
    }

    if (erroCarga) {
        return pagina(
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '32px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                <h2 style={{ color: '#0f172a', margin: '0 0 8px 0' }}>Inscrições indisponíveis</h2>
                <p style={{ color: '#64748b', margin: 0 }}>
                    Este link de inscrição não está ativo no momento. Procure o RH para mais informações.
                </p>
            </div>
        );
    }

    if (sucesso) {
        return pagina(
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '40px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                <CheckCircle2 size={56} color="#059669" style={{ marginBottom: '16px' }} />
                <h2 style={{ color: '#0f172a', margin: '0 0 8px 0' }}>Inscrição confirmada!</h2>
                <p style={{ color: '#475569', margin: 0, lineHeight: 1.6 }}>
                    <strong>{sucesso.nome}</strong>, sua inscrição para a vaga de <strong>{sucesso.vaga}</strong> foi
                    registrada. Acompanhe pelo WhatsApp os próximos passos do processo.
                </p>
            </div>
        );
    }

    return pagina(
        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '32px', border: '1px solid #e2e8f0' }}>
            <div style={{ borderLeft: `4px solid ${COR}`, paddingLeft: '14px', marginBottom: '24px' }}>
                <p style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: COR, margin: 0 }}>
                    Programa de Promoção Interna
                </p>
                <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a', margin: '4px 0 0 0' }}>{processo.nome}</h1>
                {processo.descricao && (
                    <p style={{ color: '#64748b', fontSize: '14px', margin: '8px 0 0 0', lineHeight: 1.5 }}>{processo.descricao}</p>
                )}
            </div>

            <form onSubmit={handleSubmit}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>CPF</label>
                <input
                    value={cpf}
                    onChange={(e) => setCpf(mascararCPF(e.target.value))}
                    placeholder="000.000.000-00"
                    inputMode="numeric"
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '15px', boxSizing: 'border-box', marginBottom: '18px' }}
                    required
                />

                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>Vaga desejada</label>
                <select
                    value={idVaga}
                    onChange={(e) => setIdVaga(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '15px', boxSizing: 'border-box', marginBottom: '18px', backgroundColor: '#fff' }}
                    required
                >
                    <option value="">Selecione…</option>
                    {processo.vagas.map((v) => (
                        <option key={v.ID} value={v.ID}>{v.CARGO}</option>
                    ))}
                </select>

                {erro && (
                    <div style={{ backgroundColor: '#fff1f2', color: '#be123c', padding: '12px', borderRadius: '8px', fontSize: '14px', marginBottom: '18px' }}>
                        {erro}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={enviando}
                    style={{ width: '100%', padding: '14px', borderRadius: '8px', border: 'none', backgroundColor: COR, color: '#fff', fontSize: '16px', fontWeight: 700, cursor: enviando ? 'not-allowed' : 'pointer', opacity: enviando ? 0.7 : 1 }}
                >
                    {enviando ? 'Enviando…' : 'Confirmar inscrição'}
                </button>
                <p style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', margin: '16px 0 0 0' }}>
                    A inscrição é exclusiva para funcionários ativos do Grupo Correia MC.
                </p>
            </form>
        </div>
    );
}

export default PPIInscricao;
